import React, { useEffect, useCallback } from "react";
import PullToRefresh from "pulltorefreshjs";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { PulseLoader } from "react-spinners";
import { TodaySchedule } from "../../../components";
import { DashboardWidget, GetWhoIsInRequest, IGetTimeOffBalance } from "../../../types";
import { DashboardTile, TimeOffTile, TimesheetSummaryTile } from "../../../components/mobile";
import { getBalanceByCategory, getTimeOffRequestList } from "../../../redux/features/timeoff";
import { setShowTopNav } from "../../../redux/features/mobileLayout";
import { getMyTodaysSchedule } from "../../../redux/features/schedule/ScheduleApi";
import { DateTimeUtil } from "../../../utils";
import { eachDayOfInterval, format, subDays, addDays } from "date-fns";
import { WhoIsInTile } from "../whoIsIn";
import { useNavigate } from "react-router";
import { getWhoIsWorking } from "../../../redux/features/whoIsIn";
import { getCurrentTime } from "../../../utils/employeeTimeCardhelper";
import { fetchLastPunch } from "../../../redux/features/common";
import { clearGlobalBackHandler } from "../../../hooks";
import ManagerDashboardTileContent from "../../../components/mobile/m-timesheet/ManagerTimesheetDashTileContent";

const MobileDashboard: React.FC = () => {
    const user = useSelector((state: RootState) => state.user.user);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isManager = user.is_manager;

    // Default dashboard widgets configuration
    const defaultDashboardWidgets: DashboardWidget = {
        MODULE_TIMEOFF: true,
        MY_SCHEDULE: true,
        TIMECARD: true,
        WHO_IS_IN: true,
    };

    // Merge user settings with default widgets; in case user settings are not defined, use defaults
    const { MODULE_TIMEOFF, MY_SCHEDULE, TIMECARD, WHO_IS_IN } = {
        // ...defaultDashboardWidgets,
        ...user?.dashboard_widget,
    };

    const { employees: whoIsWorkingEmp, isLoading: whoIsWorkingLoading } = useSelector(
        (state: RootState) => state.whoIsIn
    );
    const { todaysSchedule } = useSelector((state: RootState) => state.schedule);

    const fetchInitialDashboardData = async (forceRefresh: boolean = false) => {
        const promises: Promise<any>[] = [];

        if (WHO_IS_IN) {
            promises.push(
                dispatch(
                    getWhoIsWorking({
                        current_datetime: DateTimeUtil.formatDateToMySQL(new Date()),
                        forceRefresh,
                    })
                )
            );
        }

        if (MY_SCHEDULE) {
            promises.push(dispatch(getMyTodaysSchedule({
                forceRefresh
            })));
        }

        if (user?.id !== -1) {
            if (MODULE_TIMEOFF) {
                const body: IGetTimeOffBalance = {
                    category_code: "",
                    employee_id: user.id!.toString(),
                    forceRefresh
                };
                // promises.push();

                const dateRange = getDateRange();

                const getTimeOffRequestsBody = {
                    start_date: dateRange[0],
                    end_date: dateRange[dateRange.length - 1],
                    employee_id: user.id!.toString(),
                    category_code: "",
                    forceRefresh
                };

                promises.push(
                    dispatch(getBalanceByCategory(body)),
                    dispatch(getTimeOffRequestList(getTimeOffRequestsBody))
                );
            }
        }

        await Promise.all(promises);
    };

    const getDateRange = (): string[] => {
        const today = new Date();
        const start = subDays(today, 30); // 30 days before today
        const end = addDays(today, 30); // 30 days after today

        return eachDayOfInterval({ start, end }).map((date) => format(date, "yyyy-MM-dd"));
    };

    const refreshData = useCallback(async () => {
        fetchInitialDashboardData(true);
    }, [dispatch, user?.id]);

    useEffect(() => {
        if (user?.id && user.id !== -1) {
            fetchInitialDashboardData(false);
        }
    }, [user?.id]);

    useEffect(() => {
        // refreshData();

        PullToRefresh.init({
            mainElement: "#refresh-container",
            onRefresh: () => refreshData(),
            instructionsPullToRefresh: "Pull to refresh",
            instructionsReleaseToRefresh: "Release to refresh",
            getMarkup: () => `
                <div class="w-full h-16 flex items-center justify-center bg-white shadow-sm">
                    <div class="w-full flex items-center justify-center">
                    <div class="flex items-center justify-center gap-2 text-sm text-gray-600">
                        <div class="h-4 w-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                        <span>Refreshing...</span>
                    </div>
                    </div>
                </div>
                `,
            distThreshold: 40,
            distMax: 60,
            distReload: 50,

            shouldPullToRefresh() {
                const container = document.getElementById("refresh-container");
                return container?.scrollTop === 0;
            },
        });

        return () => {
            PullToRefresh.destroyAll();
            dispatch(setShowTopNav(true));
        };
    }, [refreshData]);
    useEffect(() => {
        const datetime = getCurrentTime(); // example datetime
        dispatch(fetchLastPunch(datetime));
    }, [dispatch]);

    const [loadingMessage, setLoadingMessage] = React.useState(0);
    const loadingMessages = [
        "Your dashboard is being prepared...",
        "Fetching your account details...",
        "Almost ready...",
    ];

    useEffect(() => {
        if (!user.dashboard_widget) {
            const interval = setInterval(() => {
                setLoadingMessage((prev) => (prev + 1) % loadingMessages.length);
            }, 1500);

            return () => clearInterval(interval);
        }
    }, [user.dashboard_widget]);

    if (!user.dashboard_widget) {
        return (
            <div className="flex flex-col items-center justify-center h-screen w-full bg-white">
                <PulseLoader
                    color="#3B82F6"
                    loading={true}
                    size={12}
                    speedMultiplier={0.8}
                    margin={4}
                    aria-label="Loading Dashboard"
                />
                <div
                    className="mt-6 text-gray-600 text-center text-base font-light"
                    style={{
                        animation: "fadeIn 0.3s ease-out",
                    }}
                >
                    {loadingMessages[loadingMessage]}
                </div>
                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(5px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div id="refresh-container" className="h-full overflow-y-auto bg-white">
            <div className="p-4 space-y-6">
                {/* who is in */}
                {WHO_IS_IN && <WhoIsInTile employees={whoIsWorkingEmp} isLoading={whoIsWorkingLoading} />}

                {/* my schedule + calendar */}
                {MY_SCHEDULE && <TodaySchedule />}

                {/* timeoff */}
                {MODULE_TIMEOFF && <TimeOffTile />}
                {/* <TimeOffTile /> */}
                {TIMECARD && <TimesheetSummaryTile />}
                {/* <div className="flex flex-col gap-4" onClick={() => navigate("/m-unavailability-form")}>
                    go to unavailability
                </div> */}

                {isManager && (
                    <DashboardTile
                        title="Team Timesheets"
                        onNavigate={() => {
                            clearGlobalBackHandler();
                            navigate("/m-manager-timesheet", { state: { from: "/mobile-dashboard" } });
                        }}
                        children={<ManagerDashboardTileContent />}
                    />
                )}
            </div>
        </div>
    );
};

export default MobileDashboard;
